import { useEffect, useRef, useState, type ReactNode, type Ref } from 'react';

/** Both the local ref and the caller's, on one node. */
const composeRefs =
    <T,>(...refs: (Ref<T> | undefined)[]) =>
    (node: T | null) => {
        refs.forEach((ref) => {
            if (typeof ref === 'function') ref(node);
            else if (ref) (ref as { current: T | null }).current = node;
        });
    };
import { useReducedMotion } from 'framer-motion';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { BoostResolutionOverlay } from '@modules/page-builder/component/common/upscale-warning/BoostResolutionOverlay';
import { cn } from '@rocco/utils/cn';
import { getImageAttrs } from '~/modules/images';
import {
    ARTICLE_PHOTO_ATTR,
    useArticlePhotoViewer,
} from './ArticlePhotoViewer';
import { useBannerVideo } from './ArticleBannerVideo';
import { useColourOnArrival } from '../hook/use-colour-on-arrival';
import { REVAMP_HERO_SIZES } from '../util/revamp-image-sizes';

/** The design's banner box, also the boost overlay's upscale target. */
const HERO_WIDTH = 1520;
const HERO_HEIGHT = 800;

/**
 * The premium masthead's banner runs the full page width, on the project
 * page's crop: 1200/868, the banner the project opens on, so the two mastheads
 * stand the same height.
 */
import { REVAMP_HERO_BLEED_BOX } from '../util/article-hero-box';

const BLEED_WIDTH = 1920;
const BLEED_HEIGHT = 1389;

export interface RevampHeroImageProps {
    /** The frame, for whatever is measuring the banner's place on the screen. */
    frameRef?: React.Ref<HTMLDivElement>;
    /** The overscanned picture inside it, which is what drifts. */
    mediaRef?: React.Ref<HTMLDivElement>;
    imageUrl: string;
    /** The banner's own alt text. Empty when it has none — the h1 already names the article. */
    alt: string;
    caption?: string;
    /**
     * The banner's video, where the editor uploaded one. The masthead stays
     * the still; the video plays in the article's viewer.
     */
    videoUrl?: string;
    /** Open the viewer on the banner's video at once — the `?video=1` opt-in. */
    autoplay?: boolean;
    /** LibraryImage behind the banner; carries the boost target. */
    libraryImageId?: number;
    wasUpscaled?: boolean;
    /**
     * Run edge to edge on a wider crop, as the premium masthead sets it. The
     * square corner goes with the bleed — a rounded edge against the viewport
     * has nothing to sit on.
     */
    fullBleed?: boolean;
    /**
     * Drift the picture against the page as it scrolls. The banner is the
     * article's opening image and holds the top of a long page; letting it move
     * more slowly than the copy gives that opening some depth.
     */
    parallax?: boolean;
    /**
     * The picture the reader was already looking at, when the page was
     * reached by its tile flying into this box. Painted underneath until the
     * banner's own rendition has loaded, so the flight lands on the picture
     * rather than on a blank box.
     */
    underlaySrc?: string;
    /**
     * A layer over the photograph and under the hover actions — the
     * masthead's tint and scrim, which have to grade the picture without
     * greying out the controls that sit on it.
     */
    overlay?: ReactNode;
    /**
     * Leave the caption to the page. The banner's box is also the box a
     * directory tile flies into and the edge a client badge straddles, and
     * neither wants the caption inside it.
     */
    hideCaption?: boolean;
}

/** The caption's type, for a page that draws it outside the banner's box. */
export const REVAMP_HERO_CAPTION_CLASS =
    'm-0 font-sans text-[0.75rem] leading-[1.3] tracking-[-0.03em] text-[#767676] md:w-[25rem] md:text-[0.87rem]';

/** Full-width hero banner with an offset caption, per the landscape/large module. */
export const RevampHeroImage = ({
    imageUrl,
    alt,
    caption,
    videoUrl,
    autoplay = false,
    libraryImageId,
    wasUpscaled,
    fullBleed = false,
    overlay,
    parallax = false,
    frameRef,
    mediaRef,
    underlaySrc,
    hideCaption = false,
}: RevampHeroImageProps) => {
    const { mobile } = useAppDisplayModeContext();
    const reduceMotion = useReducedMotion() ?? false;
    const parallaxRef = useRef<HTMLDivElement>(null);
    const isParallax = parallax && !reduceMotion;
    // Black and white on load, its own colour a frame later. Reduced motion
    // gets the colour straight away rather than a change it did not ask for.
    // Not when a tile flew in: the reader arrives looking at the picture in
    // colour, and greying it to colour it again would read as a fault.
    const inColour = useColourOnArrival(!reduceMotion && !underlaySrc);
    // The underlay holds until the banner's rendition has decoded. A cached
    // rendition can be complete before the load handler is attached, so the
    // element is asked as well.
    const [bannerLoaded, setBannerLoaded] = useState(false);
    const bannerRef = useRef<HTMLImageElement>(null);
    useEffect(() => {
        if (underlaySrc && bannerRef.current?.complete) setBannerLoaded(true);
    }, [underlaySrc]);
    const openPhotoViewer = useArticlePhotoViewer();
    const { open: openBannerVideo } = useBannerVideo();

    // `?video=1` opens the viewer on the banner as the page settles, which is
    // what that link has always promised: arrive and the film is playing.
    // Once — a viewer the reader then closed must not be reopened by a
    // re-render, and the flag stays set for the life of the page.
    const hasAutoOpened = useRef(false);
    useEffect(() => {
        if (!autoplay || !videoUrl || !openPhotoViewer) return;
        if (hasAutoOpened.current) return;
        hasAutoOpened.current = true;
        openBannerVideo();
    }, [autoplay, videoUrl, openPhotoViewer, openBannerVideo]);

    // No hover actions on the banner: it is the article's masthead, not one
    // of its figures — the tile-style corner disc sat over the headline's
    // photograph on both breakpoints and read as a stray control. The body
    // images keep theirs; the banner is still in the photo viewer, where the
    // viewer's own actions apply.

    // A video banner has no still to upscale, so boost is off there too, as it
    // is on the legacy banner.
    const canBoostBanner = !videoUrl && !mobile && !!libraryImageId;

    const boxWidth = fullBleed ? BLEED_WIDTH : HERO_WIDTH;
    const boxHeight = fullBleed ? BLEED_HEIGHT : HERO_HEIGHT;
    const boxClassName = fullBleed
        ? // On the phone the masthead stands tall, the directory masthead's
          // own crop, so the headline and byline ride the photograph the way
          // the project page's do; the wide banner crop left them nothing to
          // stand on.
          `w-full ${REVAMP_HERO_BLEED_BOX}`
        : // Square, like the full-bleed crop: a rounded corner against the
          // edge of the viewport has nothing to sit on.
          'aspect-[1520/800] w-full';

    const bannerImage = (
        <img
            ref={bannerRef}
            {...getImageAttrs(imageUrl, { sizes: REVAMP_HERO_SIZES })}
            // In the article's photo viewer with the body's pictures: it is
            // one of the article's images, so paging should reach it.
            {...{ [ARTICLE_PHOTO_ATTR]: '' }}
            data-article-photo-src={imageUrl}
            data-article-photo-caption={caption ?? ''}
            data-article-photo-id={libraryImageId ?? undefined}
            // The viewer builds this slide a player instead of a picture.
            data-article-photo-video={videoUrl ?? undefined}
            // A caption is rendered below as a visible figcaption, which
            // already describes the image — repeating it here would read it
            // out twice.
            alt={caption ? '' : alt}
            // The design's ratio, also carried by the aspect class — the
            // attributes reserve the box before any CSS lands.
            width={boxWidth}
            height={boxHeight}
            fetchPriority="high"
            decoding="async"
            onLoad={underlaySrc ? () => setBannerLoaded(true) : undefined}
            className={cn(
                boxClassName,
                'object-cover',
                underlaySrc && 'transition-opacity duration-300',
                underlaySrc && !bannerLoaded && 'opacity-0',
            )}
        />
    );

    /**
     * The banner in the article's photo viewer, like every other picture in
     * the body — and, where the editor uploaded one, the banner's film.
     *
     * The masthead itself stays a photograph either way. A player embedded
     * here brought its provider's own chrome with it — a title and a row of
     * controls, sitting over the headline and under the site header — and a
     * masthead that starts playing behind the words is not a masthead. The
     * disc below says there is a film, and the viewer is where it plays.
     */
    const openable = (content: ReactNode) =>
        openPhotoViewer ? (
            <button
                type="button"
                aria-label={videoUrl ? 'Play video' : 'View image full size'}
                onClick={(event) => {
                    if (videoUrl) {
                        openBannerVideo();
                        return;
                    }
                    const image = event.currentTarget.querySelector('img');
                    if (image) openPhotoViewer(image);
                }}
                className={cn(
                    'block size-full border-0 bg-transparent p-0',
                    videoUrl ? 'cursor-pointer' : 'cursor-zoom-in',
                )}
            >
                {content}
            </button>
        ) : (
            content
        );

    const media = bannerImage;

    // The actions stay hidden until the banner is hovered, the reveal the
    // legacy image shell drives from its own container rule.
    const frame = (
        <div
            ref={composeRefs(parallaxRef, frameRef)}
            className={cn(
                boxClassName,
                // `group` for the same reason the body figures carry one: the
                // action panel's strips come up on `md:group-hover`.
                'group relative overflow-hidden',
            )}
        >
            {/* The tile's rendition, under the banner until it has loaded.
                Positioned, and first, so the banner paints over it. */}
            {underlaySrc ? (
                <img
                    src={underlaySrc}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 size-full object-cover"
                />
            ) : null}
            {/* Black and white on load, then its own colour. The filter sits
                on the media, not on the frame: the frame also holds the hover
                actions, and greying those out read as disabled. */}
            <div
                style={{ filter: inColour ? 'grayscale(0)' : 'grayscale(1)' }}
                className={cn(
                    isParallax
                        ? 'absolute inset-x-0 top-[-12%] bottom-[-12%]'
                        : 'relative size-full',
                    'transition-[filter] duration-1000 ease-out motion-reduce:transition-none',
                    '[&_img]:h-full [&_img]:w-full [&_img]:object-cover',
                )}
            >
                {isParallax ? (
                    <div
                        ref={mediaRef}
                        className="size-full will-change-transform [&_img]:h-full [&_img]:w-full [&_img]:object-cover"
                    >
                        {openable(media)}
                    </div>
                ) : (
                    openable(media)
                )}
            </div>
            {overlay}
        </div>
    );

    return (
        <figure
            // The monogram parked in the left margin reads this to know when
            // the banner has risen far enough to cover it.
            data-article-banner=""
            className="m-0 flex w-full flex-col gap-4 md:gap-6"
        >
            {/* Edge to edge. It used to open on the design's 200px margins
                and grow as the reader scrolled; the banner leads the page now,
                so there is nothing above it to grow away from. The caption
                below keeps a margin of its own. */}
            <div className="w-full">
                {canBoostBanner && libraryImageId ? (
                    <BoostResolutionOverlay
                        libraryImageId={libraryImageId}
                        currentImageUrl={imageUrl}
                        displayWidth={boxWidth}
                        displayHeight={boxHeight}
                        wasUpscaled={!!wasUpscaled}
                        className="w-full"
                        // Boost keeps the bottom-right corner it holds on the body
                        // images and every other detail page; the hover actions own
                        // the top right, so the two never share a corner.
                    >
                        {frame}
                    </BoostResolutionOverlay>
                ) : (
                    frame
                )}
            </div>
            {/* The picture's own line under it, set as the body's figures
                set theirs, so the banner reads as the first of them. */}
            {caption && !hideCaption && (
                <figcaption className={REVAMP_HERO_CAPTION_CLASS}>
                    {caption}
                </figcaption>
            )}
        </figure>
    );
};
